import { Component, Input, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';

export interface RuntimeRiskData {
  service: string;
  critical?: number;
  high?: number;
  medium?: number;
  low?: number;
  informational?: number;
}

@Component({
  selector: 'app-runtime-risk-breakdown',
  templateUrl: './runtime-risk-breakdown.component.html',
  styleUrls: ['./runtime-risk-breakdown.component.scss'],
})
export class RuntimeRiskBreakdownComponent implements OnChanges {
  @Input() public title: string;
  @Input() public tooltip: string;
  @Input() public selectedFilters: ISelectedFilters;
  @Input() public seriesData: RuntimeRiskData[];

  @ViewChild(BaseChartDirective) public chart?: BaseChartDirective;

  public chartData: ChartData<'bar'> = {
    labels: [] as string[],
    datasets: [],
  };
  public chartOptions: ChartOptions<'bar'>;
  public noData = false;

  constructor() {
    this._initializeChartOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['seriesData'] && this.seriesData && this.seriesData.length > 0) {
      this._populateChartData(this.seriesData);

      if (this.chart) {
        this.chart.update();
      }
    }
  }

  private _populateChartData(data: RuntimeRiskData[]): void {
    const services = data.map((item) => this._getServiceDisplayName(item.service));
    const severityLevels = ['critical', 'high', 'medium', 'low', 'informational'];
    const severityColors = {
      critical: '#bd0d00',
      high: '#ef574c',
      medium: '#F98300',
      low: '#d69a00',
      informational: '#c4d7f5',
    };

    const datasets: ChartDataset<'bar'>[] = severityLevels.map((severity) => ({
      label: this._getSeverityDisplayName(severity),
      data: data.map((item) => (item[severity as keyof RuntimeRiskData] as number) || 0),
      backgroundColor: severityColors[severity as keyof typeof severityColors],
      hoverBackgroundColor: severityColors[severity as keyof typeof severityColors],
      borderRadius: 4,
      borderSkipped: false,
    }));

    this.chartData = {
      labels: services,
      datasets: datasets.filter((dataset) => dataset.data.some((value) => typeof value === 'number' && value > 0)),
    };

    this.noData = data.length === 0 || datasets.every((dataset) => dataset.data.every((value) => value === 0));
  }

  private _initializeChartOptions(): void {
    this.chartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      indexAxis: 'x',
      font: {
        family: 'Inter',
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            color: '#696879',
            font: {
              size: 12,
            },
          },
        },
        y: {
          stacked: true,
          grid: {
            display: true,
            color: '#F5F5F5',
          },
          ticks: {
            color: '#696879',
            font: {
              size: 12,
            },
            callback: (value: number | string | null) => {
              return value != null ? Number(value).toLocaleString() : '';
            },
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          align: 'center',
          labels: {
            usePointStyle: true,
            padding: 16,
            font: {
              size: 12,
              family: 'Inter',
            },
            color: '#696879',
          },
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#FFFFFF',
          titleColor: '#000000',
          bodyColor: '#696879',
          borderColor: '#E5E5E5',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (tooltipItems) => {
              return this._getServiceDisplayName(tooltipItems[0].label);
            },
            label: (context) => {
              const label = context.dataset.label || '';
              const value = Number(context.parsed.y).toLocaleString();
              return `${label}: ${value}`;
            },
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
    };
  }

  private _getServiceDisplayName(service: string): string {
    const serviceNames: { [key: string]: string } = {
      copilotStudio: 'Copilot Studio',
      m365Copilot: 'Microsoft 365 Copilot',
      chatGptEnterprise: 'ChatGPT Enterprise',
      vertexAi: 'Vertex AI',
    };

    return serviceNames[service] || service;
  }

  private _getSeverityDisplayName(severity: string): string {
    const severityNames: { [key: string]: string } = {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      informational: 'Informational',
    };

    return severityNames[severity] || severity;
  }
}
